import axios from 'axios'
import downloadFile from 'js-file-download'
import startsWith from 'lodash/startsWith'
import endsWith from 'lodash/endsWith'
import event from 'services/event'
import { generatePath } from 'react-router'
import { getToken } from 'helpers/auth'
import { hasFiles, objectToFormData } from 'helpers/form'
import { EVENT_401, EVENT_503 } from 'constants/events'

/**
 * Extract file name from header content-disposition
 *
 * @param {Object} headers
 * @return {String}
 */
export const extractFileName = (headers) => {
  const contentDisposition = headers && headers['content-disposition']
  //Convert to get file name
  if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const matches = filenameRegex.exec(contentDisposition)
    if (matches != null && matches[1]) {
      return matches[1].replace(/['"]/g, '')
    }
  }

  return 'download'
}

// Create new axios instance
const instance = axios.create()

// Add a request interceptor
instance.interceptors.request.use((config) => {
  // Mocking test json api
  if (startsWith(config.url, '/static/api/')) {
    // Set prefix for api request
    config.baseURL = process.env.PUBLIC_URL
    // Change body data to query params
    if (config.data && typeof config.data === 'object') {
      config.params = { ...config.params, ...config.data }
    }
    // Change method to get
    config.method = 'GET'
  }

  // Token
  const token = getToken()
  if (token && !config.anonymous) {
    config.headers['Authorization'] = 'Bearer ' + token
  }

  // Config request for upload file
  if (hasFiles(config.data)) {
    // Set data
    config.data = objectToFormData(config.data)

    // Set multipart headers
    config.headers['Content-Type'] = 'multipart/form-data'
  }

  // Download or view file content
  if (config.downloadFile || config.viewFile) {
    config.responseType = 'blob'
  }

  // Read more about path to regex here: https://github.com/pillarjs/path-to-regexp
  config.url = generatePath(config.url, { ...config.params, ...config.data })

  return config
})

// Add a response interceptor
instance.interceptors.response.use(
  async (response) => {
    const { data, config, headers } = response

    // Handle blob response
    if (data instanceof Blob) {
      const fileName = extractFileName(headers)

      // Download file
      if (config.downloadFile) {
        return downloadFile(data, fileName)
      }

      // View file
      if (config.viewFile) {
        const viewableExtensions = ['.mp4', '.pdf', '.jpg', 'jpeg', '.png']

        if (viewableExtensions.some((ext) => endsWith(fileName, ext))) {
          return window.open(URL.createObjectURL(data))
        }

        return downloadFile(data, fileName)
      }

      return response
    }

    // Extract data
    return config.raw ? response : data
  },
  async (error) => {
    const { response, config = {} } = error

    // 401 case
    if (response && response.status === 401 && !config.silent) {
      event.emit(EVENT_401)
    }

    // 503 case
    if (response && response.status === 503 && !config.silent) {
      event.emit(EVENT_503)
    }

    return Promise.reject(error)
  }
)

export default instance
